import React from 'react'
import { Link, Navigate } from 'react-router-dom'
import { 
  MapPin, 
  Clock, 
  Navigation, 
  Star, 
  Shield, 
  CheckCircle,
  ArrowRight,
  Smartphone,
  CreditCard,
  Mail
} from 'lucide-react'
import {
  SUPPORT_EMAIL,
  COMPANY_NAME,
  SUPPORT_PHONE,
  SUPPORT_PHONE_TEL,
  COMPANY_ADDRESS_LINE1,
  COMPANY_ADDRESS_LINE2,
} from '../constants/contact'
import { getPlanPrices } from '../constants/pricing'
import { useLocalization } from '../contexts/LocalizationContext'
import { useAuth } from '../contexts/AuthContext'
import { Logo } from '../components/Logo'

export function Landing() {
  const { t, formatPrice, config } = useLocalization()
  const { user } = useAuth()
  const { hourlyRate, monthlyPrice, dailyPrice } = getPlanPrices(config?.currency)

  // Si l'utilisateur est connecté, rediriger vers le dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 gap-2">
            <div className="flex items-center min-w-0">
              <Logo width={160} height={40} />
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <Link
                to="/auth"
                className="hidden sm:inline text-gray-600 hover:text-paybyphone-green font-medium transition-colors"
              >
                {t('common.signIn')}
              </Link>
              <Link
                to="/auth"
                className="btn-primary text-sm sm:text-base px-3 sm:px-4 py-2 whitespace-nowrap"
              >
                {t('common.getStarted')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-paybyphone-light to-white py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                {t('landing.title')}
              </h1>
              <p className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                {t('landing.subtitle').replace(/[$€]?\s?49[.,]99\s?€?\/(month|mois|Monat|mes|mese)/, `${formatPrice()}/${t('common.monthly')}`)}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
                <Link
                  to="/auth"
                  className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 inline-flex items-center justify-center"
                >
                  {t('landing.heroButton')} - {formatPrice()} / {t('common.monthly')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <div className="text-sm text-gray-500 flex items-center">
                  <Shield className="h-4 w-4 mr-2" />
                  {t('landing.securePayment')}
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-r from-paybyphone-green to-green-600 rounded-2xl p-8 shadow-2xl">
                <div className="bg-white rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <MapPin className="h-6 w-6 text-paybyphone-green mr-2" />
                    <span className="font-semibold text-gray-900">{t('landing.mock.title')}</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-sm font-medium">{t('landing.mock.saved')}</span>
                      <CheckCircle className="h-5 w-5 text-paybyphone-green flex-shrink-0" />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <span className="text-sm font-medium">{t('landing.mock.timeRemaining')}</span>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-semibold">1h 30min</span>
                    </div>
                    <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                      <Smartphone className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-600">{t('landing.mock.reminder')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-paybyphone-green" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('landing.features.saveTime.title')}
              </h3>
              <p className="text-gray-600">
                {t('landing.features.saveTime.description')}
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('landing.features.smartMap.title')}
              </h3>
              <p className="text-gray-600">
                {t('landing.features.smartMap.description')}
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Navigation className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('landing.features.navigation.title')}
              </h3>
              <p className="text-gray-600">
                {t('landing.features.navigation.description')}
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('landing.features.favorites.title')}
              </h3>
              <p className="text-gray-600">
                {t('landing.features.favorites.description')}
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('landing.features.notifications.title')}
              </h3>
              <p className="text-gray-600">
                {t('landing.features.notifications.description')}
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('landing.features.support.title')}
              </h3>
              <p className="text-gray-600">
                {t('landing.features.support.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-paybyphone-green to-green-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('landing.stats.title')}
            </h2>
            <p className="text-xl text-green-100">
              {t('landing.stats.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-2">15-20 min</div>
              <div className="text-green-100">{t('landing.stats.timeSaved')}</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-2">98%</div>
              <div className="text-green-100">{t('landing.stats.satisfaction')}</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-2">{t('common.currency') === '€' ? '200€+' : '$200+'}</div>
              <div className="text-green-100">{t('landing.stats.savings')}</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-2">50k+</div>
              <div className="text-green-100">{t('landing.stats.users')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('landing.pricing.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('landing.pricing.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Pay As You Go */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-200 flex flex-col">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('subscriptionV6.direct.payAsYouGo')}</h3>
                <div className="text-4xl font-bold text-paybyphone-green mb-1">{formatPrice(hourlyRate)}</div>
                <div className="text-gray-600 text-sm">{t('subscriptionV6.direct.perHour')}</div>
              </div>
              <div className="space-y-3 mb-6 flex-1">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-paybyphone-green mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{t('landing.pricing.features.map')}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-paybyphone-green mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{t('landing.pricing.features.favorites')}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-paybyphone-green mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{t('landing.pricing.features.notifications')}</span>
                </div>
              </div>
              <Link to="/auth" className="btn-primary text-base px-6 py-3 w-full inline-flex items-center justify-center">
                {t('common.getStarted')}
              </Link>
            </div>

            {/* Daily */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-paybyphone-green relative flex flex-col">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-paybyphone-green text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                {t('subscriptionV6.direct.popular')}
              </div>
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('subscriptionV6.direct.dailyPlan')}</h3>
                <div className="text-4xl font-bold text-paybyphone-green mb-1">{formatPrice(dailyPrice)}</div>
                <div className="text-gray-600 text-sm">{t('subscriptionV6.direct.perDay')}</div>
              </div>
              <div className="space-y-3 mb-6 flex-1">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-paybyphone-green mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{t('landing.pricing.features.map')}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-paybyphone-green mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{t('landing.pricing.features.navigation')}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-paybyphone-green mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{t('landing.pricing.features.notifications')}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-paybyphone-green mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{t('landing.pricing.features.cancel')}</span>
                </div>
              </div>
              <Link to="/auth" className="btn-primary text-base px-6 py-3 w-full inline-flex items-center justify-center">
                <CreditCard className="mr-2 h-5 w-5" />
                {t('common.subscribeNow')}
              </Link>
            </div>

            {/* Monthly */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-200 flex flex-col">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('subscriptionV6.direct.monthlyPlan')}</h3>
                <div className="text-4xl font-bold text-paybyphone-green mb-1">{formatPrice(monthlyPrice)}</div>
                <div className="text-gray-600 text-sm">{t('subscriptionV6.direct.perMonth')}</div>
              </div>
              <div className="space-y-3 mb-6 flex-1">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-paybyphone-green mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{t('landing.pricing.features.map')}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-paybyphone-green mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{t('landing.pricing.features.navigation')}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-paybyphone-green mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{t('landing.pricing.features.support')}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-paybyphone-green mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{t('landing.pricing.features.cancel')}</span>
                </div>
              </div>
              <Link to="/auth" className="btn-primary text-base px-6 py-3 w-full inline-flex items-center justify-center">
                <CreditCard className="mr-2 h-5 w-5" />
                {t('common.subscribeNow')}
              </Link>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            {t('landing.securePayment')}
          </p>

          {/* Security Notice */}
          <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <Shield className="h-6 w-6 text-blue-600 mr-3 mt-1" />
              <div>
                <h4 className="text-lg font-semibold text-blue-900 mb-2">
                  {t('landing.pricing.securityNotice.title')}
                </h4>
                <p className="text-blue-700">
                  {t('landing.pricing.securityNotice.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 bg-paybyphone-dark">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 sm:mb-6">
            {t('landing.cta.title')}
          </h2>
          <p className="text-base sm:text-xl text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto">
            {t('landing.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
            <Link
              to="/auth"
              className="bg-paybyphone-green text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-medium hover:bg-green-600 transition-colors duration-200 inline-flex items-center justify-center"
            >
              {t('landing.heroButton')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="bg-white text-paybyphone-dark px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-gray-100 transition-colors duration-200 inline-flex items-center justify-center border-2 border-white"
            >
              <Mail className="mr-2 h-5 w-5" />
              {t('footer.contactSupport')}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-2xl font-bold text-paybyphone-green mb-4">
                ParkingBolt
              </h3>
              <p className="text-gray-400 mb-4">
                {t('footer.description')}
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('footer.legal')}</h4>
              <div className="space-y-2">
                <Link to="/legal" className="text-gray-400 hover:text-white block transition-colors">
                  {t('navigation.legalNotice')}
                </Link>
                <Link to="/terms" className="text-gray-400 hover:text-white block transition-colors">
                  {t('navigation.terms')}
                </Link>
                <Link to="/privacy" className="text-gray-400 hover:text-white block transition-colors">
                  {t('navigation.privacy')}
                </Link>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('footer.support')}</h4>
              <div className="space-y-2">
                <Link to="/contact" className="text-gray-400 hover:text-white block transition-colors">
                  {t('footer.contactSupport')}
                </Link>
                <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-gray-400 hover:text-white block transition-colors">
                  {SUPPORT_PHONE}
                </a>
                <Link to="/subscription" className="text-gray-400 hover:text-white block transition-colors">
                  {t('navigation.cancelSubscription')}
                </Link>
                <Link to="/auth" className="text-gray-400 hover:text-white block transition-colors">
                  {t('common.signIn')}
                </Link>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-400 mb-2">
              © 2026 {COMPANY_NAME}. {t('footer.allRightsReserved')}
            </p>
            <p className="text-gray-500 text-sm mb-2">
              {COMPANY_NAME} — {COMPANY_ADDRESS_LINE1}, {COMPANY_ADDRESS_LINE2}
            </p>
            <p className="text-gray-500 text-sm">
              {t('footer.needHelp')}{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green hover:text-green-400 transition-colors">{SUPPORT_EMAIL}</a>
              {' · '}
              <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-paybyphone-green hover:text-green-400 transition-colors">{SUPPORT_PHONE}</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
